import { useState, useEffect } from 'react';
import { Button, Flex, Text, theme } from '../../../2022-component-library';
import { ResponsiveContainer, LineChart, Line, Legend, Tooltip, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useTestContext } from '../../../context/testContext';
import { useAppointment } from '../../../context/appointmentContext';
import { client } from '../../../helpers/api-client';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { confirmNavigation } from '../../../helpers/functions';
import { useResultData } from '../../../helpers/useResultData';
import styled from '@emotion/styled';
import { usePostMessage } from '../../../context/messageContext';

const ResponsiveContrastSensitivityResults = ({ setActiveTab, updateStep, goBackToCompletedTests }) => {
  const [tableResults, setTableResults] = useState([]);
  const [graphResults, setGraphResults] = useState([]);
  const [showGraph, setShowGraph] = useState(false);
  const [offerGraphOption, setOfferGraphOption] = useState(false);
  const { testData } = useTestContext();
  const viewingLocalTestResults = testData.contrastSensitivityResults?.length;
  const smallScreen = useMediaQuery('(max-width: 700px)');
  const postMessage = usePostMessage();
  const { appointment } = useAppointment();
  const { clearAllResults } = useResultData();

  const StyledTable = styled.table`
    th {
      padding: 1rem;
      background-color: ${theme.colors.primary};
      color: #fff;
      font-size: 1.25rem;
    }

    td {
      text-align: center;
      font-size: 1.25rem;
      padding: 0.625rem;
    }

    tbody tr:nth-of-type(even) {
      background-color: #f3ffff;
    }
  `;

  useEffect(() => {
    const getRemoteTestResults = async () => {
      const testId = testData.testResultsId;
      client(`api/appointment-test-result/${testId}`).then(
        (data) => {
          setTableResults(JSON.parse(data.result_data));
        },
        (error) => {
          console.error(error);
        },
      );
    };

    if (!viewingLocalTestResults) getRemoteTestResults();
  }, [viewingLocalTestResults, testData.testResultsId]);

  const restartTest = () => {
    confirmNavigation('restart').then(() => {
      clearAllResults();
      return postMessage({ type: 'beginTestAgain' });
    });
  };

  useEffect(() => {
    const rawResults = testData.contrastSensitivityResults || [];

    rawResults
      .sort((a, b) => {
        return a.logmarTested > b.logmarTested ? 1 : b.logmarTested > a.logmarTested ? -1 : 0;
      })
      .reverse();

    const parsedResults = Object.values(
      rawResults.reduce((resultsObj, { logmarTested, ...eyeResults }) => {
        if (!resultsObj[logmarTested]) resultsObj[logmarTested] = {};
        resultsObj[logmarTested] = {
          logmarTested,
          ...resultsObj[logmarTested],
          ...eyeResults,
        };
        return resultsObj;
      }, {}),
    );

    if (viewingLocalTestResults) setTableResults(parsedResults);
  }, [testData.contrastSensitivityResults, viewingLocalTestResults]);

  useEffect(() => {
    let parsedResults = [];
    let filteredResults = [];
    tableResults.forEach((result) => {
      const { logmarTested, leftEye, rightEye, bothEyes } = result;
      parsedResults.push({
        name: logmarTested,
        Left: parseFloat(leftEye?.toFixed(2)),
        Right: parseFloat(rightEye?.toFixed(2)),
        Binocular: parseFloat(bothEyes?.toFixed(2)),
      });
    });

    parsedResults.forEach((result) => {
      filteredResults.push(
        Object.entries(result).reduce((resultObj, [eyeTested, value]) => (value ? ((resultObj[eyeTested] = value), resultObj) : resultObj), {}),
      );
    });

    setGraphResults(filteredResults);
  }, [tableResults]);

  useEffect(() => {
    const results = tableResults || [];
    if (
      results.filter((result) => {
        return result.hasOwnProperty('leftEye');
      }).length >= 2
    ) {
      setOfferGraphOption(true);
    }
    if (
      results.filter((result) => {
        return result.hasOwnProperty('rightEye');
      }).length >= 2
    ) {
      setOfferGraphOption(true);
    }
    if (
      results.filter((result) => {
        return result.hasOwnProperty('bothEyes');
      }).length >= 2
    ) {
      setOfferGraphOption(true);
    }
  }, [testData.contrastSensitivityResults, tableResults]);

  const sendTestResults = () => {
    const rawResults = testData.contrastSensitivityResults || [];
    const { corrected } = testData;

    rawResults
      .sort((a, b) => {
        return a.logmarTested > b.logmarTested ? 1 : b.logmarTested > a.logmarTested ? -1 : 0;
      })
      .reverse();

    const parsedResults = Object.values(
      rawResults.reduce((resultsObj, { logmarTested, ...eyeResults }) => {
        if (!resultsObj[logmarTested]) resultsObj[logmarTested] = {};
        resultsObj[logmarTested] = {
          logmarTested,
          ...resultsObj[logmarTested],
          ...eyeResults,
        };
        return resultsObj;
      }, {}),
    );

    const sendTestResultData = {
      appointment_eye_test: testData.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify([...parsedResults, { corrected: corrected }]),
    };

    if (window.confirm('Are you sure you want to save the test results?') === true) {
      client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
        () => {
          clearAllResults();
          setActiveTab('results');
        },
        (error) => {
          // setState({ ...state, error: error.message });
          console.error('oh no, login failed', error);
        },
      );
    }
  };

  return tableResults.length ? (
    <Flex column centered width='95vw' maxWidth='700px' maxHeight='700px'>
      <Flex height='65vh' width='100%' justifyContent='center' alignItems={!smallScreen && 'center'} overflowY='auto' border='1px solid black'>
        {!showGraph ? (
          <StyledTable>
            <thead>
              <tr>
                <th>{`logMAR`}</th>
                <th>Right Eye</th>
                <th>Left Eye</th>
                <th>Binocular</th>
              </tr>
            </thead>
            <tbody>
              {tableResults.map((result) => {
                const { logmarTested, leftEye, rightEye, bothEyes } = result;
                return (
                  <tr key={`${logmarTested}`}>
                    <td>{logmarTested}</td>
                    <td>{rightEye?.toFixed(2) || '-'}</td>
                    <td>{leftEye?.toFixed(2) || '-'}</td>
                    <td>{bothEyes?.toFixed(2) || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        ) : (
          <ResponsiveContainer>
            <LineChart data={graphResults} margin={{ right: 25 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey='name' />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type='monotone' dataKey='Right' stroke='orange' strokeWidth={2} connectNulls />
              <Line type='monotone' dataKey='Left' stroke='blue' strokeWidth={2} connectNulls animationDuration={1000} />
              <Line type='monotone' dataKey='Binocular' stroke='teal' strokeWidth={2} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Flex>
      <Flex centered marginTop='1rem' width='100%'>
        <Button
          variant='outlined'
          width='calc(25vw - 2rem)'
          marginRight='1rem'
          onClick={goBackToCompletedTests ? goBackToCompletedTests : () => restartTest()}
        >
          {goBackToCompletedTests ? 'Back' : 'Restart Test'}
        </Button>
        {offerGraphOption ? (
          <Button backgroundColor={theme.colors.callToAction} marginRight='1rem' onClick={() => setShowGraph(!showGraph)}>
            {!showGraph ? 'Show Graph' : 'Show Table'}
          </Button>
        ) : null}

        {updateStep && (
          <Button minWidth='13em' onClick={() => sendTestResults()}>
            Save Test Results
          </Button>
        )}
      </Flex>
    </Flex>
  ) : (
    <Flex column centered height='100%'>
      <Text>No test results to display.</Text>
      <Button
        marginTop='2rem'
        onClick={
          goBackToCompletedTests
            ? goBackToCompletedTests
            : () => {
                updateStep(testData.testBothEyes ? 'bothEyesTest' : testData.testLeftEye ? 'leftEyeTest' : 'rightEyeTest');
              }
        }
      >
        Back
      </Button>
    </Flex>
  );
};

export default ResponsiveContrastSensitivityResults;
